import React, { useEffect, useCallback, useRef } from 'react';
import { Clock, Pencil, Save, Trash2 } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';

interface TimelineEvent {
  date: string;
  event: string;
}

interface VerticalTimelineProps {
  events: TimelineEvent[];
  isLoading: boolean;
  error?: string | null;
  isEnglish?: boolean;
  onUpdateEvents?: (events: TimelineEvent[]) => void;
}

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div role="alert" className="text-red-600 py-4">
      <p>Something went wrong:</p>
      <pre className="text-sm">{error.message}</pre>
      <button
        onClick={resetErrorBoundary}
        className="mt-2 text-blue-600 hover:text-blue-800"
      >
        Try again
      </button>
    </div>
  );
}

const TimelineContent: React.FC<VerticalTimelineProps> = ({ 
  events,
  isLoading, 
  error, 
  isEnglish,
  onUpdateEvents 
}) => {
  const [editingEvents, setEditingEvents] = React.useState<TimelineEvent[]>([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Ensure clean up of edit state
  useEffect(() => {
    return () => {
      setIsEditing(false);
      setEditingEvents([]);
    };
  }, []);

  // Update editing events when source events change
  useEffect(() => {
    if (!isEditing) {
      setEditingEvents(events);
    }
  }, [events, isEditing]);

  const startEditing = useCallback(() => {
    setEditingEvents([...events]);
    setIsEditing(true);
  }, [events]);

  const handleEventChange = useCallback((index: number, field: 'date' | 'event', value: string) => {
    setEditingEvents(prev => {
      const newEvents = [...prev];
      newEvents[index] = { ...newEvents[index], [field]: value };
      return newEvents;
    });
  }, []);

  const handleDeleteEvent = useCallback((index: number) => {
    setEditingEvents(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(() => {
    if (onUpdateEvents) {
      onUpdateEvents(editingEvents);
    }
    setIsEditing(false);
  }, [editingEvents, onUpdateEvents]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditingEvents([]);
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!timelineRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    const timelineItems = timelineRef.current.querySelectorAll('.timeline-item');
    timelineItems.forEach(item => observer.observe(item));

    return () => {
      timelineItems.forEach(item => observer.unobserve(item));
    };
  }, [events.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2">
          {isEnglish ? 'Analyzing document events...' : 'Analisando eventos do documento...'}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="text-red-600 py-4">
        {error}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div role="status" className="text-gray-500 italic py-4">
        {isEnglish ? 'No events found in the document' : 'Nenhum evento encontrado no documento'}
      </div>
    );
  }

  const displayEvents = isEditing ? editingEvents : events;

  return (
    <div className="space-y-4" ref={timelineRef}>
      <div className="flex justify-end mb-4 print:hidden">
        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              type="button"
            >
              {isEnglish ? 'Cancel' : 'Cancelar'}
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
              type="button"
            >
              <Save className="w-4 h-4" />
              {isEnglish ? 'Save Changes' : 'Salvar Alterações'}
            </button>
          </div>
        ) : (
          <button
            onClick={startEditing}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
            type="button"
          >
            <Pencil className="w-4 h-4" />
            {isEnglish ? 'Edit Timeline' : 'Editar Linha do Tempo'}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {displayEvents.map((event, index) => (
          <div 
            key={`${event.date}-${index}`}
            className="timeline-item flex opacity-0 transition-opacity duration-500 ease-in-out"
            role="listitem"
          >
            <div className="flex-shrink-0 w-8 relative">
              <div 
                className="absolute top-1.5 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"
                aria-hidden="true"
              >
                <Clock className="w-4 h-4 text-blue-600" />
              </div>
              {index < displayEvents.length - 1 && (
                <div 
                  className="absolute left-[15px] top-10 bottom-[-24px] w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="flex-grow pl-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={event.date}
                      onChange={(e) => handleEventChange(index, 'date', e.target.value)}
                      className="w-full p-2 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-label={isEnglish ? "Event date" : "Data do evento"}
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={event.event}
                        onChange={(e) => handleEventChange(index, 'event', e.target.value)}
                        className="flex-1 p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        aria-label={isEnglish ? "Event description" : "Descrição do evento"}
                      />
                      <button
                        onClick={() => handleDeleteEvent(index)}
                        className="p-2 text-red-600 hover:text-red-800 rounded-md hover:bg-red-50"
                        type="button"
                        aria-label={isEnglish ? "Delete event" : "Excluir evento"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-blue-600 font-medium mb-1">{event.date}</div>
                    <div className="text-gray-700">{event.event}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const VerticalTimeline: React.FC<VerticalTimelineProps> = (props) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <TimelineContent {...props} />
    </ErrorBoundary>
  );
};